import { useEffect, useRef } from "react";

interface UseWavyTextAnimationOptions {
  isActive: boolean;
  text: string;
  /** Delay before starting the animation (ms) */
  startDelay?: number;
  /** Delay between each letter animation (ms) */
  letterDelay?: number;
}

/**
 * Hook for creating wavy text animations
 * Safely creates spans for each character and applies staggered animations
 */
export const useWavyTextAnimation = ({
  isActive,
  text,
  startDelay = 200,
  letterDelay = 60,
}: UseWavyTextAnimationOptions) => {
  const textElementRef = useRef<HTMLHeadingElement>(null);
  const timeoutsRef = useRef<number[]>([]);

  useEffect(() => {
    if (!isActive) return;

    const timer = setTimeout(() => {
      const textElement = textElementRef.current;
      if (!textElement) return;

      // Split text into spans using safe DOM APIs
      textElement.textContent = ""; // Clear existing content
      const fragment = document.createDocumentFragment();

      for (const character of text) {
        const span = document.createElement("span");
        span.textContent = character; // Safe character escaping
        fragment.appendChild(span);
      }

      textElement.appendChild(fragment);

      // Apply wavy animation to each letter
      Array.from(textElement.children).forEach((span, index) => {
        const timeoutId = setTimeout(
          () => {
            span.classList.add("wavy");
          },
          index * letterDelay + startDelay,
        ) as unknown as number;
        timeoutsRef.current.push(timeoutId);
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      // Clear all per-letter animation timeouts
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [isActive, text, startDelay, letterDelay]);

  return {
    textElementRef,
  };
};
